import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Loader from '../../modules/layout/components/Loader';
import FiltersCard from '../../modules/layout/components/FiltersCard';
import Pagination from '../../modules/layout/components/Pagination';
import { useToast } from '../../modules/layout/components/ToastContext';
import { post } from '../_request';
import { Formik, Form, Field } from 'formik';
import SortDirection from '../../modules/layout/components/SortDirection';
import { IProfileFilterDto, IProfileItemDto } from '../../interfaces/IUtilities';
import ApprovalToggle from './ApprovalToggle';
import { useAuth, userHasRole } from "../../../setup/auth/authService";
import { AdminWeb, ManageRoles } from "../../constants/constants";

export default function AgentUsersList() {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<IProfileItemDto[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [hasSearched, setHasSearched] = useState(false);
  const [sortField, setSortField] = useState<'username' | 'fullname'>('username');
  const [asc, setAsc] = useState(true);
  
  const { user } = useAuth();
  const [isAdminWeb, setIsAdminWeb] = useState(false);
  const [isManageRoles, setIsManageRoles] = useState(false);
          
    useEffect(() => {
      const checkUserRole = async () => {
        const hasAdminWebRole = await userHasRole(AdminWeb);
        const hasManageRolesRole = await userHasRole(ManageRoles);
        setIsAdminWeb(hasAdminWebRole);
        setIsManageRoles(hasManageRolesRole);
      };  
      if (user && !user.expired) {
        checkUserRole();
      }
    }, [user]);
  

  const performSearch = async (body: IProfileFilterDto) => {
    if (loading) return { success: false };
    const hasAnyFilter = Boolean(
      body.Username || body.FirstName || body.LastName || body.Fullname || body.Email || body.MobileNumber || body.ProfileId
    );

    if (!hasAnyFilter) {
      showToast?.(t('SelectAtLeastOneFilter'), 'warning');
      return { success: false };
    }

    setHasSearched(true);
    setLoading(true);
    setError(null);
    try {
      const result: any = await post('/api/UserEvents/findagentprofiles'
        , { ...body, Category: 'Agent' }
      );
      const payload = result?.data?.Data ?? result?.data ?? result;
      const items = Array.isArray(payload?.Items)
        ? payload.Items
        : Array.isArray(payload)
        ? payload
        : [];
      setResults(items as IProfileItemDto[]);

      if ((!items || items.length === 0) && showToast) {
        showToast(t('NoDataFound'), 'info');
      }
      return { success: true };
    } catch (e: any) {
      setError(String(e?.message ?? e));
      showToast?.(t('ConnectionError', { error: String(e) }), 'error');
    } finally {
      setLoading(false);
    }
    return { success: false };
  };

  const handleReset = () => {
    setResults([]);
    setError(null);
    setHasSearched(false);
  };

  return (
    <div className="container mt-4">
      <h4 className="mb-3">{t('AgentUsersList')}</h4>

      <FiltersCard isActive={true}>
        <Formik
          initialValues={{
            Username: '',
            FirstName: '',
            LastName: '',
            Fullname: '',
            Email: '',
            MobileNumber: '',
            ProfileId: '',
            Exact: false,
          } as IProfileFilterDto}
          onSubmit={async (values) => await performSearch(values)}
        >
          {({ handleSubmit, resetForm }) => (
            <Form onSubmit={handleSubmit}>
              <div className="mb-3 row g-2 align-items-end">
                <div className="col-md-3">
                  <label className="form-label">{t('Username')}</label>
                  <Field name="Username" className="form-control" />
                </div>
                <div className="col-md-3">
                  <label className="form-label">{t('Email')}</label>
                  <Field name="Email" className="form-control" />
                </div>
                <div className="col-md-3">
                  <label className="form-label">{t('MobileNumber')}</label>
                  <Field name="MobileNumber" className="form-control" />
                </div>
                <div className="col-sm-12 col-md-6 col-lg-4 col-xl-3 mt-2 d-flex align-items-center">
                  <div className="form-check me-3 mb-2">
                    <Field type="checkbox" name="Exact" className="form-check-input" id="exact" />
                    <label className="form-check-label" htmlFor="exact">{t('Exact')}</label>
                  </div>
                  <div className="mb-2 d-flex">
                    <button type="submit" className="btn gg-btn gg-btn-submit me-2">{t('Search')}</button>
                    <button
                      type="button"
                      className="btn btn-secondary text-white"
                      onClick={() => {
                        resetForm();
                        handleReset();
                      }}
                    >
                      {t('Reset')}
                    </button>
                  </div>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </FiltersCard>

      {error && <div className="alert alert-danger">{error}</div>}
      {loading && <Loader />}
      {!hasSearched && !loading && (
        <div className="alert alert-info">{t('SetFiltersAndSearch')}</div>
      )}

      {hasSearched && (
        <>
          <div className="table-responsive">
            <table className="table table-striped table-sm">
              <thead>
                <tr>
                  <th style={{ cursor: 'pointer' }}
                    onClick={() => { if (sortField === 'username') setAsc(!asc); else { setSortField('username'); setAsc(true); } }}>
                    {t('Username')} <SortDirection isActive={sortField === 'username'} order={asc ? 'asc' : 'desc'} />
                  </th>
                  <th>{t('FullName')}</th>
                  <th>{t('Email')}</th>
                  <th>{t('Actions')}</th>
                  <th>{t('Roles')}</th>
                </tr>
              </thead>
              <tbody className="table-group-divider">
                {results.length === 0 && !loading && (
                  <tr>
                    <td colSpan={4} className="text-center">{t('NoDataFound')}</td>
                  </tr>
                )}

                {(() => {
                  const sorted = results.slice().sort((a, b) => {
                    const valA = ((sortField === 'username' ? a.Username : a.Name ?? a.Fullname) ?? '').toLowerCase();
                    const valB = ((sortField === 'username' ? b.Username : b.Name ?? b.Fullname) ?? '').toLowerCase();
                    return asc ? valA.localeCompare(valB) : valB.localeCompare(valA);
                  });
                  const startIndex = (page - 1) * pageSize;
                  const paged = sorted.slice(startIndex, startIndex + pageSize);

                  return paged.map((p, idx) => (
                    <tr key={p.ProfileId ?? startIndex + idx} >
                      <td>{p.Username}</td>
                      <td>{p.Name ?? p.Fullname}</td>
                      <td>{p.Email}</td>
                      <td className="toggle-cell">
                          {p.ProfileId && (isManageRoles && isAdminWeb) &&(
                            <ApprovalToggle
                              profileId={p.ProfileId}
                              currentState={(p.ProfileState === 'Active' ? 'Active' : 'Inactive') as 'Active' | 'Inactive'}
                              onChange={(newState) =>
                                setResults((prev) => prev.map((r) => r.ProfileId === p.ProfileId ? { ...r, ProfileState: newState } : r))
                              }
                            />
                          )}
                       </td>
                       <td>
                        <button className="btn gg-btn-black  me-2"
                            onClick={() =>navigate(`/manage-accounts/assign-profile-role/${encodeURIComponent(String(p.ProfileId))}`)}>
                            {t('ViewRoles')}
                        </button>
                       </td>
                    </tr>
                  ));
                })()}
              </tbody>
            </table>
          </div>

          <Pagination
            currentPage={page}
            onPageChange={setPage}
            totalItems={results.length}
            pageSize={pageSize}
            onPageSizeChange={(s) => {
              setPageSize(s);
              setPage(1);
            }}
          />
        </>
      )}
    </div>
  );
}
